import { createLogger } from "./logger";

const log = createLogger("eventLog");

/** Kind of record in the unified stream. Pushes and pulls share one ordering. */
export type EntryKind =
  | "event"
  | "request"
  | "error"
  | "lifecycle"
  | "log"
  | "interceptor";

/** A single ordered record. `t` is monotonic; `seq` survives eviction so gaps are detectable. */
export type LogEntry = {
  seq: number;
  ts: string;
  t: number;
  kind: EntryKind;
  moduleId: string;
  name: string;
  contactId?: string;
  payload?: unknown;
  durationMs?: number;
  error?: { message: string; name?: string; stack?: string };
};

export type LogEntryInput = Omit<LogEntry, "seq" | "ts" | "t">;

export type EventLogStats = {
  count: number;
  evicted: number;
  capacity: number;
};

const DEFAULT_CAPACITY = 5000;

/** Bounded, ordered, append-only record of everything the kit observed. */
export class EventLog {
  private buffer: LogEntry[] = [];
  private seqCounter = 0;
  private evicted = 0;
  private readonly capacity: number;
  private readonly subscribers = new Set<
    (entries: readonly LogEntry[]) => void
  >();

  constructor(opts: { capacity?: number } = {}) {
    this.capacity = Math.max(1, opts.capacity ?? DEFAULT_CAPACITY);
  }

  /** Appends an entry, stamping seq/ts/t, evicting oldest-first at capacity. */
  append(input: LogEntryInput): LogEntry {
    const entry: LogEntry = {
      ...input,
      seq: ++this.seqCounter,
      ts: new Date().toISOString(),
      t: performance.now(),
    };
    this.buffer.push(entry);
    if (this.buffer.length > this.capacity) {
      this.buffer.splice(0, this.buffer.length - this.capacity);
      this.evicted += 1;
      if (this.evicted === 1)
        log.warn("capacity reached; evicting oldest entries", {
          capacity: this.capacity,
        });
    }
    this.notify();
    return entry;
  }

  /** Current entries, oldest first. */
  entries(): readonly LogEntry[] {
    return this.buffer.slice();
  }

  /** Drops all entries. The seq counter is deliberately not reset. */
  clear(): void {
    this.buffer = [];
    this.evicted = 0;
    this.notify();
  }

  /** Occupancy and eviction counters, so a truncated session is never read as complete. */
  stats(): EventLogStats {
    return {
      count: this.buffer.length,
      evicted: this.evicted,
      capacity: this.capacity,
    };
  }

  /** Registers a change listener. Returns an unsubscribe function. */
  subscribe(cb: (entries: readonly LogEntry[]) => void): () => void {
    this.subscribers.add(cb);
    return () => this.subscribers.delete(cb);
  }

  /**
   * Installs previously persisted entries without re-stamping them, and advances the seq
   * counter past the highest restored value so new appends stay monotonic.
   *
   * The eviction count is RECOVERED, not reset: `seq` starts at 1 and is never reused, so the
   * gap below the lowest surviving `seq` is a lower bound on how many entries are gone —
   * including any this call's own capacity trim just dropped. Inference is used in preference
   * to a persisted counter because it also works for payloads written before this field
   * existed. `evicted` may be passed when the caller knows an exact figure; the larger of the
   * two wins, so a restored session can never claim to be more complete than it is.
   */
  restore(entries: readonly LogEntry[], evicted?: number): void {
    this.buffer = [...entries].slice(-this.capacity);
    this.seqCounter = entries.reduce((m, e) => Math.max(m, e.seq), 0);
    const lowest = this.buffer.reduce(
      (m, e) => Math.min(m, e.seq),
      Number.POSITIVE_INFINITY,
    );
    const inferred = Number.isFinite(lowest) ? Math.max(0, lowest - 1) : 0;
    this.evicted = Math.max(evicted ?? 0, inferred);
    this.notify();
  }

  private notify(): void {
    const snapshot = this.buffer.slice();
    for (const cb of this.subscribers) {
      try {
        cb(snapshot);
      } catch (e) {
        log.error("subscriber threw", e);
      }
    }
  }
}
